import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { Fragment, useContext, useEffect, useState } from "react";
import NumberFormat from "react-number-format";
import Select from "react-select";
import Switch from "react-switch";
import { Button, ButtonGroup, Col, Input, Label, Row, Table } from "reactstrap";
import { ItemDropdownMenu, OrderDropdownMenu, Pager, SortHeader } from ".";
import { api, constants, dateHelpers, reportHelpers, UserContext } from "../utils";
import { SpinnerButton } from './';
import { toast } from "react-toastify";

export default function OrderGrid(props) {
   const { currentUser } = useContext(UserContext);
   const [working, setWorking] = useState(false);
   const [shippingOptions, setShippingOptions] = useState([]);
   const [editIndex, setEditIndex] = useState(null);
   const [editCopy, setEditCopy] = useState(null);
   const [editAllLineDates, setEditAllLineDates] = useState(false);

   useEffect(() => {
      api
         .fetch("Reference/GetShippingCompanyList")
         .then((response) => {
            setShippingOptions(
               _.chain(response.data.companies)
                  .map((cmp) => {
                     return { label: cmp.ddlDescription, value: cmp.ddlValue };
                  })
                  .sortBy((x) => {
                     return x.label;
                  })
                  .value()
            );
         })
         .catch(api.catchHandler);
   }, []);

   function allowEdit(index) {
      setEditIndex(index);
      setEditCopy(_.assign({}, props.data[index]));
   }

   function cancelEdit() {
      setEditIndex(null);
      setEditCopy(null);
      setEditAllLineDates(false);
   }

   function validateEditCopy() {
      if (editCopy === null) {
         return false;
      }
      if (!editCopy.requiredShipDate) {
         return true;
      }
      if (!editCopy.shipMethod) {
         return true;
      }
      const fulfilledAmt = editCopy.qtyFulfilled <= 0 ? 1 : editCopy.qtyFulfilled;
      if (editCopy.releaseQty < fulfilledAmt || editCopy.releaseQty > 9999999) {
         return true;
      }
      if (editCopy.unitPrice <= 0) {
         return true;
      }
      return false;
   }

   function handleChange(field, value) {
      const copyUpdate = _.assign({}, editCopy);
      copyUpdate[field] = value;
      setEditCopy(copyUpdate);
   }

   function updateOrderLine() {
      setWorking(true);
      const payload = _.chain({})
         .assign(editCopy)
         //.pickBy((_v, k) => editCopy[k] !== props.data[editIndex][k])
         .value();
      if (payload.unitPrice) {
         payload["currencyDecimals"] = editCopy.currencyDecimals;
      }
      if (payload.releaseQty) {
         payload["qtyRemaining"] = editCopy.qtyRemaining;
      }
      if (payload.requiredShipDate) {
         payload["editAllLineDates"] = editAllLineDates;
      }

      if (_.keys(payload).length) {
         payload["orderId"] = editCopy.orderId;
         payload["itemNumber"] = editCopy.itemNumber;

         api
            .post(`Customer/UpdateOrderLine`, payload)
            .then((response) => {
               if (response.data.success) {
                  props.pagerCallback(props.pageNumber);
               } else {
                  if (_.isString(response?.data?.message)) {
                     toast.warning(response?.data?.message);
                  }
               }
            })
            .catch(api.catchHandler)
            .finally(() => {
               cancelEdit();
            });
      } else {
         cancelEdit();
      }
      setWorking(false);
   }

   function getExcel() {
      setWorking(true);
      props.exportXlsxCallback();
      setWorking(false);
   }

   const canEdit = currentUser?.isAPUCS;

   return (
      <>
         <Row className="align-items-center mb-1">
            <Col className="transparent-background">
               <Pager
                  callBack={props.pagerCallback}
                  sizeCallback={props.sizeCallback ? props.sizeCallback : null}
                  pageNumber={props.pageNumber}
                  pageSize={props.pageSize ? props.pageSize : null}
                  totalPages={props.totalPages}
                  downloading={working}
                  allowAll={props.allowAll}
                  defaultPageSize={props.defaultPageSize}
               />
            </Col>
         </Row>
         <Table size="sm" className="data-table">
            <thead className="table-header">
               <tr>
                  <th style={{ width: "80px" }}>
                     <SortHeader
                        displayName="Order"
                        field="orderId"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "orderId"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "80px" }}>
                     <SortHeader
                        displayName="Order Date"
                        field="documentDate"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "documentDate"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "120px" }}>
                     <SortHeader
                        displayName="PO"
                        field="customerPONumber"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "customerPONumber"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "150px" }}>
                     <SortHeader
                        displayName="Customer P/N"
                        field="itemNumber"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "itemNumber"}
                        callback={props.sortCallback}
                     />
                  </th>
                  {!currentUser?.isAnyCustomerTypeRole ? (
                     <th style={{ width: "170px" }}>
                        <SortHeader
                           displayName="Item"
                           field="itemDescription"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "itemDescription"}
                           callback={props.sortCallback}
                        />
                     </th>
                  ) : null}
                  <th style={{ width: "7%" }}>
                     <SortHeader
                        displayName="Req Ship Date"
                        field="requiredShipDate"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "requiredShipDate"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: canEdit ? "11%" : "13%" }}>
                     <SortHeader
                        displayName="Ship Method"
                        field="shipMethod"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "shipMethod"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "120px" }}>
                     <SortHeader
                        displayName="Ship To"
                        field="stCityState"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "stCityState"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "5%" }}>
                     <SortHeader
                        displayName={<span>Order<br />Qty</span>}
                        field="qtyRemaining"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "qtyRemaining"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "5%" }}>
                     <SortHeader
                        displayName={<span>Release<br />Qty</span>}
                        field="releaseQty"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "releaseQty"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "5%" }}>
                     <SortHeader
                        displayName={<span>Applied<br />Qty</span>}
                        field="qtyFulfilled"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "qtyFulfilled"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "5%" }}>
                     <SortHeader
                        displayName={<span>Available<br />Qty</span>}
                        field="qtyAvailable"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "qtyAvailable"}
                        callback={props.sortCallback}
                     />
                  </th>
                  <th style={{ width: "5%" }}>
                     <SortHeader
                        displayName={<span>Remaining<br />Qty</span>}
                        field="qtyBackOrdered"
                        sortDirection={props.sortDirection}
                        sorted={props.sortField === "qtyBackOrdered"}
                        callback={props.sortCallback}
                     />
                  </th>
                  {canEdit ? (
                     <th style={{ width: "100px" }}>
                        <SortHeader
                           displayName={<span>Unit<br />Price</span>}
                           field="unitPrice"
                           sortDirection={props.sortDirection}
                           sorted={props.sortField === "unitPrice"}
                           callback={props.sortCallback}
                        />
                     </th>
                  ) : (
                     <th />
                  )}
                  <th>
                     <ButtonGroup className="float-end">
                        {props.data &&
                           props.data.length &&
                           props.exportXlsxCallback && (
                              <Button
                                 title="Download as .xlsx"
                                 className="shredButtonExcel float-end ms-2"
                                 onClick={getExcel}
                                 disabled={working}
                              >
                                 <FontAwesomeIcon icon="file-excel" size="lg" />
                              </Button>
                           )}
                        {props.data && props.data.length && props.exportPdfCallback && (
                           <Button
                              title="Download as .pdf"
                              className="shredButton float-end"
                              onClick={props.exportPdfCallback}
                              disabled={working}
                           >
                              <FontAwesomeIcon icon="file-pdf" size="lg" />
                           </Button>
                        )}
                     </ButtonGroup>
                  </th>
               </tr>
            </thead>
            <tbody>
               {_.map(props.data, (datum, index) => {
                  const isEditing = editIndex === index;
                  const readOnly = !isEditing;
                  const shipDate = isEditing
                     ? dateHelpers.formatDateForServer(editCopy.requiredShipDate)
                     : dateHelpers.toLongDateString(datum.requiredShipDate);

                  return (
                     <tr key={index} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                        <OrderDropdownMenu
                           invoiceNumber={null}
                           orderId={datum.orderId}
                           attachmentCount={datum.soAttachmentCount}
                           orderType={datum.orderType}
                           itemNumber={datum.itemNumber}
                        />
                        <td
                           className="data-cell"
                           children={dateHelpers.toLongDateString(datum.documentDate)}
                        />
                        <td className="data-cell" children={datum.customerPONumber} />
                        <td
                           className="data-cell"
                           onClick={() => reportHelpers.getCustomerSpecReport(datum.customerId, datum.itemNumber)}
                           title="Print Customer Spec Report"
                        >
                           <span className="shredLink fw-bold cursor-pointer">
                              {datum.customerItemNumber}
                           </span>
                        </td>
                        {!currentUser?.isAnyCustomerTypeRole ? (
                           <ItemDropdownMenu
                              itemNumber={datum.itemNumber}
                              itemDescription={datum.itemWithDesc}
                              attachmentCount={datum.itemAttachmentCount}
                           />
                        ) : null}
                        <td className="data-cell">
                           {isEditing ? (
                              <Fragment>
                                 <Row>
                                    <Col>
                                       <Input
                                          type="date"
                                          onChange={(e) => handleChange("requiredShipDate", e.target.value)}
                                          value={shipDate}
                                          style={{ fontSize: "1em" }}
                                       />
                                    </Col>
                                 </Row>
                                 <Row className="align-items-center">
                                    <Col xs={3}>
                                       <Switch
                                          className="mt-3"
                                          height={20}
                                          width={48}
                                          checked={editAllLineDates}
                                          onChange={() => setEditAllLineDates(!editAllLineDates)}
                                       />
                                    </Col>
                                    <Col xs={9}>
                                       <Label
                                          className="pt-2 ps-2 pb-0 mb-0 "
                                          children="Update date on all line items"
                                       />
                                    </Col>
                                 </Row>
                              </Fragment>
                           ) : (
                              <span>{shipDate}</span>
                           )}
                        </td>
                        <td className="data-cell">
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              className={`${isEditing ? "" : "readonly-input"}`}
                              options={shippingOptions}
                              value={
                                 isEditing
                                    ? _.find(shippingOptions, (opt) => opt.value === editCopy.shipMethod)
                                    : _.find(shippingOptions, (opt) => opt.value === datum.shipMethod)
                              }
                              onChange={(e) => handleChange("shipMethod", e.value)}
                              readOnly={readOnly}
                              placeholder={readOnly ? "" : "Select..."}
                              isSearchable
                              styles={isEditing ? {} : constants.READONLY_SELECT_STYLE}
                           />
                        </td>
                        <td className="data-cell" children={datum.stCityState} />
                        <td className="data-cell" children={datum.qtyRemaining.toLocaleString()} />
                        <td className="data-cell">
                           {readOnly ? (
                              datum.releaseQty.toLocaleString()
                           ) : (
                              <Input
                                 type="number"
                                 bsSize="sm"
                                 className={`px-1`}
                                 value={editCopy.releaseQty}
                                 onChange={(e) => handleChange("releaseQty", parseInt(e.target.value)) }
                              />
                           )}
                        </td>
                        <td className="data-cell" children={datum.qtyFulfilled.toLocaleString()} />
                        <td
                           className="data-cell"
                           children={datum.qtyAvailable.toLocaleString()}
                           onClick={
                              !currentUser?.isAnyCustomerTypeRole
                                 ? () => reportHelpers.getCaseLotInventoryReport(datum.itemNumber, null)
                                 : null
                           }
                        >
                           <span className={!currentUser?.isAnyCustomerTypeRole ? "shredLink fw-bold cursor-pointer" : ""}>
                              {datum.qtyAvailable.toLocaleString()}
                           </span>
                        </td>
                        <td className="data-cell" children={datum.qtyBackOrdered.toLocaleString()} />
                        {canEdit && (
                           <td className="data-cell px-0" style={{ width: "100px" }}>
                              <NumberFormat
                                 className={`${isEditing ? "" : "readonly-input"}`}
                                 style={{ fontSize: ".95em", width: "100px" }}
                                 prefix="$"
                                 thousandSeparator={true}
                                 decimalSeparator="."
                                 decimalScale={2}
                                 //decimalScale={!datum.currencyDecimals || datum.currencyDecimals < 2 ? 2 : datum.currencyDecimals}
                                 fixedDecimalScale={true}
                                 allowNegative={false}
                                 onValueChange={(vals) => {
                                    if (!vals.value) {
                                       handleChange("unitPrice", 0);
                                    } else {
                                       handleChange("unitPrice", parseFloat(vals.value));
                                    }
                                 }}
                                 value={isEditing ? editCopy.unitPrice : datum.unitPrice}
                                 readOnly={readOnly}
                              />
                           </td>
                        )}
                        {canEdit && (
                           <td className="data-cell">
                              <ButtonGroup className="width95px">
                                 <SpinnerButton
                                    className={`icon-button me-1 ${isEditing ? "" : "float-end"}`}
                                    title={isEditing ? "Save changes" : "Edit line"}
                                    color={isEditing ? "success" : "info"}
                                    onClick={isEditing ? updateOrderLine : () => allowEdit(index)}
                                    disabled={
                                       (editIndex !== null && readOnly) ||
                                       validateEditCopy() ||
                                       working
                                    }
                                    on={
                                       working ||
                                       validateEditCopy() ||
                                       (editIndex !== null && readOnly)
                                    }
                                 >
                                    <FontAwesomeIcon
                                       className="whiteIcon"
                                       icon={isEditing ? "check" : "edit"}
                                    />
                                 </SpinnerButton>
                                 <Button
                                    className={`icon-button ${isEditing ? "" : "vis-hidden"}`}
                                    color="secondary"
                                    title="Discard changes"
                                    onClick={cancelEdit}
                                    disabled={editIndex !== null && readOnly}
                                 >
                                    <FontAwesomeIcon className="whiteIcon" icon="times" />
                                 </Button>
                              </ButtonGroup>
                           </td>
                        )}
                     </tr>
                  );
               })}
            </tbody>
            <tfoot className="table-footer">
               <tr>
                  <td
                     className={`fw-bold`}
                     colSpan={!currentUser?.isAnyCustomerTypeRole ? "8" : "7"}
                     children="Totals Across All Pages:"
                  />
                  <td className={`fw-bold`}>
                     {props.totals.orderedTotal.toLocaleString()}
                  </td>
                  <td className={`fw-bold`}>
                     {props.totals.releaseTotal.toLocaleString()}
                  </td>
                  <td className={`fw-bold`}>
                     {props.totals.appliedTotal.toLocaleString()}
                  </td>
                  <td />
                  <td className={`fw-bold`}>
                     {props.totals.remainingTotal.toLocaleString()}
                  </td>
                  {canEdit && <td colSpan="2" />}
               </tr>
            </tfoot>
         </Table>
      </>
   );
}
